/*
    TeamBreakdown Component
    This component is used to display the team breakdown data for a given game.
    For MLB, it will display: 
        - Overall record (Wins, Losses, WinPercentage)
        - Home record (HomeWins, HomeLosses, HomeWinPercentage)
        - Away record (AwayWins, AwayLosses, AwayWinPercentage)
        - Division, DivisionRecord, DivisionRank
        - Day record (DayWins, DayLosses, DayWinPercentage)
        - Night record (NightWins, NightLosses, NightWinPercentage)
        - League, LeagueRank
        - RunsScored, RunsAgainst, RunDifferential (Calculated)
        - Streak (negative numbers represent losing streaks)
        - LastTenGamesWins, LastTenGamesLosses, LastTenGamesWinPercentage
        - GamesBehind

    For NFL, it will display:
        - Overall record (Wins, Losses, Percentage)
        - Home record (HomeWins, HomeLosses, HomeWinPercentage - calculated)
        - Away record (AwayWins, AwayLosses, AwayWinPercentage - calculated)
        - Division, DivisionRecord, DivisionRank
        - Conference, ConferenceRecord, ConferenceRank
        - PointsFor, PointsAgainst, NetPoints
        - Streak (negative numbers represent losing streaks)

    For NBA, it will display:
        - Overall record (Wins, Losses, Percentage)
        - Home record (HomeWins, HomeLosses, HomeWinPercentage - calculated)
        - Away record (AwayWins, AwayLosses, AwayWinPercentage - calculated)
        - Division, DivisionRecord, DivisionRank
        - Conference, ConferenceRecord, ConferenceRank
        - Streak (negative numbers represent losing streaks)
        - LastTenWins, LastTenLosses, LastTenWinPercentage - calculated
        - GamesBack
        - PointsPerGameFor, PointsPerGameAgainst, PointsPerGameDifferential (Calculated)

    This component uses a unified comparative layout where both teams' stats are displayed together 
    in each section for easier comparison, with clear team identification.

    
    Props: 
        - game: the game object
        - sport: the sport of the game
        - homeStandings: the home team's standings
        - awayStandings: the away team's standings
*/

import React from 'react';

// Helper functions for calculations and formatting
const calculatePercentage = (wins, losses, ties=0) => {
    if (!wins && !losses) return 0;
    const totalGames = (wins || 0) + (losses || 0) + (ties || 0);
    if (totalGames === 0) return 0;
    return ((wins || 0) / totalGames) * 100;
};

const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return 'N/A';
    return `${percentage.toFixed(1)}%`;
};

const formatRecord = (wins, losses, ties=0) => {
    const w = wins || 0;
    const l = losses || 0;
    const t = ties || 0;
    if(t > 0){
        return `${w}-${l}-${t}`;
    }else{
        return `${w}-${l}`;
    }
};

const formatStreak = (streak) => {
    if (!streak || streak === 0) return 'N/A';
    const prefix = streak > 0 ? 'W' : 'L';
    return `${prefix}${Math.abs(streak)}`;
};

const safeGet = (obj, path, defaultValue = 'N/A') => {
    if (!obj) return defaultValue;
    const value = obj[path];
    return value !== null && value !== undefined ? value : defaultValue;
};

const formatDifferential = (for_score, against_score) => {
    const diff = (for_score - against_score).toFixed(2);
    if(diff > 0){
        return `+${diff}`;
    }else{
        return `${diff}`;
    }
};

const TeamBreakdown = ({ game, sport, homeStandings, awayStandings, homeTeam, awayTeam}) => {
    // Validate props
    if (!game || !sport || !homeStandings || !awayStandings) {
        return null;
    }

    const sportLower = sport.toLowerCase();

    // Component to render a team comparison section as a table
    const TeamComparisonSection = ({ title, children }) => (
        <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-6 text-center">{title}</h3>
            <div className="flow-root">
                <div className="overflow-hidden">
                    <div className="inline-block w-full align-middle">
                        <div className="overflow-hidden shadow border border-gray-200 dark:border-gray-600 rounded-lg">
                            <table className="min-w-full w-full table-fixed border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th scope="col" className="w-[50%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">
                                            Metric
                                        </th>
                                        <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">
                                            {homeTeam}
                                        </th>
                                        <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-3.5 text-left text-xs md:text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">
                                            {awayTeam}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {children}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Component to render individual stat row
    const StatRow = ({ label, homeValue, awayValue }) => (
        <tr>
            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">
                {label}
            </td>
            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">
                {homeValue}
            </td>
            <td className="border-b border-gray-200 dark:border-gray-700 truncate px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-100">
                {awayValue}
            </td>
        </tr>
    );

    // MLB-specific sections
    const renderMLBSections = () => (
        <>
            <TeamComparisonSection title="Overall Performance">
                <StatRow 
                    label="Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'Wins', 0), safeGet(homeStandings, 'Losses', 0))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'Wins', 0), safeGet(awayStandings, 'Losses', 0))} 
                />
                <StatRow 
                    label="Win %" 
                    homeValue={formatPercentage(safeGet(homeStandings, 'Percentage', 0) * 100)} 
                    awayValue={formatPercentage(safeGet(awayStandings, 'Percentage', 0) * 100)} 
                />
                <StatRow 
                    label="Scored" 
                    homeValue={safeGet(homeStandings, 'RunsScored')} 
                    awayValue={safeGet(awayStandings, 'RunsScored')} 
                />
                <StatRow 
                    label="Against" 
                    homeValue={safeGet(homeStandings, 'RunsAgainst')} 
                    awayValue={safeGet(awayStandings, 'RunsAgainst')} 
                />
                <StatRow 
                    label="Differential" 
                    homeValue={formatDifferential(safeGet(homeStandings, 'RunsScored', 0), safeGet(homeStandings, 'RunsAgainst', 0))} 
                    awayValue={formatDifferential(safeGet(awayStandings, 'RunsScored', 0), safeGet(awayStandings, 'RunsAgainst', 0))} 
                />
                <StatRow 
                    label="Streak" 
                    homeValue={safeGet(homeStandings, 'Streak')} 
                    awayValue={safeGet(awayStandings, 'Streak')} 
                />
            </TeamComparisonSection>

            <TeamComparisonSection title="Division & League">
                <StatRow 
                    label="Division" 
                    homeValue={safeGet(homeStandings, 'Division')} 
                    awayValue={safeGet(awayStandings, 'Division')} 
                />
                <StatRow 
                    label="Division Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'DivisionWins', 0), safeGet(homeStandings, 'DivisionLosses', 0))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'DivisionWins', 0), safeGet(awayStandings, 'DivisionLosses', 0))} 
                />
                <StatRow 
                    label="Division Rank" 
                    homeValue={safeGet(homeStandings, 'DivisionRank')} 
                    awayValue={safeGet(awayStandings, 'DivisionRank')} 
                />
                <StatRow 
                    label="League" 
                    homeValue={safeGet(homeStandings, 'League')} 
                    awayValue={safeGet(awayStandings, 'League')} 
                />
                <StatRow 
                    label="League Rank" 
                    homeValue={safeGet(homeStandings, 'LeagueRank')} 
                    awayValue={safeGet(awayStandings, 'LeagueRank')} 
                />
                <StatRow 
                    label="Games Behind" 
                    homeValue={safeGet(homeStandings, 'GamesBehind')} 
                    awayValue={safeGet(awayStandings, 'GamesBehind')} 
                />
            </TeamComparisonSection>

            <TeamComparisonSection title="Home/Away Performance">
                <StatRow 
                    label="Home Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'HomeWins'), safeGet(homeStandings, 'HomeLosses'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'HomeWins'), safeGet(awayStandings, 'HomeLosses'))} 
                />
                <StatRow 
                    label="Home Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'HomeWins', 0), safeGet(homeStandings, 'HomeLosses', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'HomeWins', 0), safeGet(awayStandings, 'HomeLosses', 0)))} 
                />
                <StatRow 
                    label="Away Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'AwayWins'), safeGet(homeStandings, 'AwayLosses'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'AwayWins'), safeGet(awayStandings, 'AwayLosses'))} 
                />
                <StatRow 
                    label="Away Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'AwayWins', 0), safeGet(homeStandings, 'AwayLosses', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'AwayWins', 0), safeGet(awayStandings, 'AwayLosses', 0)))} 
                />
            </TeamComparisonSection>

            <TeamComparisonSection title="Day/Night Performance">
                <StatRow 
                    label="Day Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'DayWins'), safeGet(homeStandings, 'DayLosses'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'DayWins'), safeGet(awayStandings, 'DayLosses'))} 
                />
                <StatRow 
                    label="Day Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'DayWins', 0), safeGet(homeStandings, 'DayLosses', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'DayWins', 0), safeGet(awayStandings, 'DayLosses', 0)))} 
                />
                <StatRow 
                    label="Night Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'NightWins'), safeGet(homeStandings, 'NightLosses'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'NightWins'), safeGet(awayStandings, 'NightLosses'))} 
                />
                <StatRow 
                    label="Night Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'NightWins', 0), safeGet(homeStandings, 'NightLosses', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'NightWins', 0), safeGet(awayStandings, 'NightLosses', 0)))} 
                />
            </TeamComparisonSection>

            <TeamComparisonSection title="Recent Performance">
                <StatRow 
                    label="Last 10" 
                    homeValue={formatRecord(safeGet(homeStandings, 'LastTenGamesWins'), safeGet(homeStandings, 'LastTenGamesLosses'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'LastTenGamesWins'), safeGet(awayStandings, 'LastTenGamesLosses'))} 
                />
                <StatRow 
                    label="Last 10 Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'LastTenGamesWins', 0), safeGet(homeStandings, 'LastTenGamesLosses', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'LastTenGamesWins', 0), safeGet(awayStandings, 'LastTenGamesLosses', 0)))} 
                />
            </TeamComparisonSection>
        </>
    );

    // NFL-specific sections
    const renderNFLSections = () => (
        <>
            <TeamComparisonSection title="Overall Performance">
                <StatRow 
                    label="Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'Wins', 0), safeGet(homeStandings, 'Losses', 0), safeGet(homeStandings, 'Ties', 0))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'Wins', 0), safeGet(awayStandings, 'Losses', 0), safeGet(awayStandings, 'Ties', 0))} 
                />
                <StatRow 
                    label="Win %" 
                    homeValue={formatPercentage(safeGet(homeStandings, 'Percentage', 0) * 100)} 
                    awayValue={formatPercentage(safeGet(awayStandings, 'Percentage', 0) * 100)} 
                />
                <StatRow 
                    label="Streak" 
                    homeValue={formatStreak(safeGet(homeStandings, 'Streak'))} 
                    awayValue={formatStreak(safeGet(awayStandings, 'Streak'))} 
                />
                <StatRow 
                    label="Points For" 
                    homeValue={safeGet(homeStandings, 'PointsFor')} 
                    awayValue={safeGet(awayStandings, 'PointsFor')} 
                />
                <StatRow 
                    label="Points Against" 
                    homeValue={safeGet(homeStandings, 'PointsAgainst')} 
                    awayValue={safeGet(awayStandings, 'PointsAgainst')} 
                />
                <StatRow 
                    label="Net Points" 
                    homeValue={safeGet(homeStandings, 'NetPoints')} 
                    awayValue={safeGet(awayStandings, 'NetPoints')} 
                />
            </TeamComparisonSection>

            <TeamComparisonSection title="Division & Conference">
                <StatRow 
                    label="Division" 
                    homeValue={safeGet(homeStandings, 'Division')} 
                    awayValue={safeGet(awayStandings, 'Division')} 
                />
                <StatRow 
                    label="Division Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'DivisionWins', 0), safeGet(homeStandings, 'DivisionLosses', 0), safeGet(homeStandings, 'DivisionTies', 0))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'DivisionWins', 0), safeGet(awayStandings, 'DivisionLosses', 0), safeGet(awayStandings, 'DivisionTies', 0))} 
                />
                <StatRow 
                    label="Division Rank" 
                    homeValue={safeGet(homeStandings, 'DivisionRank')} 
                    awayValue={safeGet(awayStandings, 'DivisionRank')} 
                />
                <StatRow 
                    label="Conference" 
                    homeValue={safeGet(homeStandings, 'Conference')} 
                    awayValue={safeGet(awayStandings, 'Conference')} 
                />
                <StatRow 
                    label="Conference Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'ConferenceWins', 0), safeGet(homeStandings, 'ConferenceLosses', 0), safeGet(homeStandings, 'ConferenceTies', 0))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'ConferenceWins', 0), safeGet(awayStandings, 'ConferenceLosses', 0), safeGet(awayStandings, 'ConferenceTies', 0))} 
                />
                <StatRow 
                    label="Conference Rank" 
                    homeValue={safeGet(homeStandings, 'ConferenceRank')} 
                    awayValue={safeGet(awayStandings, 'ConferenceRank')} 
                />
            </TeamComparisonSection>

            <TeamComparisonSection title="Home/Away Performance">
                <StatRow 
                    label="Home Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'HomeWins'), safeGet(homeStandings, 'HomeLosses'), safeGet(homeStandings, 'HomeTies'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'HomeWins'), safeGet(awayStandings, 'HomeLosses'), safeGet(awayStandings, 'HomeTies'))} 
                />
                <StatRow 
                    label="Home Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'HomeWins', 0), safeGet(homeStandings, 'HomeLosses', 0), safeGet(homeStandings, 'HomeTies', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'HomeWins', 0), safeGet(awayStandings, 'HomeLosses', 0), safeGet(awayStandings, 'HomeTies', 0)))} 
                />
                <StatRow 
                    label="Away Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'AwayWins'), safeGet(homeStandings, 'AwayLosses'), safeGet(homeStandings, 'AwayTies'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'AwayWins'), safeGet(awayStandings, 'AwayLosses'), safeGet(awayStandings, 'AwayTies'))} 
                />
                <StatRow 
                    label="Away Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'AwayWins', 0), safeGet(homeStandings, 'AwayLosses', 0), safeGet(homeStandings, 'AwayTies', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'AwayWins', 0), safeGet(awayStandings, 'AwayLosses', 0), safeGet(awayStandings, 'AwayTies', 0)))} 
                />
            </TeamComparisonSection>
        </>
    );

    // NBA-specific sections
    const renderNBASections = () => (
        <>
            <TeamComparisonSection title="Overall Performance">
                <StatRow 
                    label="Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'Wins', 0), safeGet(homeStandings, 'Losses', 0))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'Wins', 0), safeGet(awayStandings, 'Losses', 0))} 
                />
                <StatRow 
                    label="Win %" 
                    homeValue={formatPercentage(safeGet(homeStandings, 'Percentage', 0) * 100)} 
                    awayValue={formatPercentage(safeGet(awayStandings, 'Percentage', 0) * 100)} 
                />
                <StatRow 
                    label="Streak" 
                    homeValue={safeGet(homeStandings, 'StreakDescription')} 
                    awayValue={safeGet(awayStandings, 'StreakDescription')} 
                />
                <StatRow 
                    label="Games Back" 
                    homeValue={safeGet(homeStandings, 'GamesBack')} 
                    awayValue={safeGet(awayStandings, 'GamesBack')} 
                />
                <StatRow 
                    label="PPG For" 
                    homeValue={safeGet(homeStandings, 'PointsPerGameFor')} 
                    awayValue={safeGet(awayStandings, 'PointsPerGameFor')} 
                />
                <StatRow 
                    label="PPG Against" 
                    homeValue={safeGet(homeStandings, 'PointsPerGameAgainst')} 
                    awayValue={safeGet(awayStandings, 'PointsPerGameAgainst')} 
                />
                <StatRow 
                    label="PPG Differential" 
                    homeValue={formatDifferential(safeGet(homeStandings, 'PointsPerGameFor', 0), safeGet(homeStandings, 'PointsPerGameAgainst', 0))} 
                    awayValue={formatDifferential(safeGet(awayStandings, 'PointsPerGameFor', 0), safeGet(awayStandings, 'PointsPerGameAgainst', 0))} 
                />
            </TeamComparisonSection>

            <TeamComparisonSection title="Division & Conference">
                <StatRow 
                    label="Division" 
                    homeValue={safeGet(homeStandings, 'Division')} 
                    awayValue={safeGet(awayStandings, 'Division')} 
                />
                <StatRow 
                    label="Division Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'DivisionWins', 0), safeGet(homeStandings, 'DivisionLosses', 0))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'DivisionWins', 0), safeGet(awayStandings, 'DivisionLosses', 0))} 
                />
                <StatRow 
                    label="Division Rank" 
                    homeValue={safeGet(homeStandings, 'DivisionRank')} 
                    awayValue={safeGet(awayStandings, 'DivisionRank')} 
                />
                <StatRow 
                    label="Conference" 
                    homeValue={safeGet(homeStandings, 'Conference')} 
                    awayValue={safeGet(awayStandings, 'Conference')} 
                />
                <StatRow 
                    label="Conference Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'ConferenceWins', 0), safeGet(homeStandings, 'ConferenceLosses', 0))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'ConferenceWins', 0), safeGet(awayStandings, 'ConferenceLosses', 0))} 
                />
                <StatRow 
                    label="Conference Rank" 
                    homeValue={safeGet(homeStandings, 'ConferenceRank')} 
                    awayValue={safeGet(awayStandings, 'ConferenceRank')} 
                />
            </TeamComparisonSection>

            <TeamComparisonSection title="Home/Away Performance">
                <StatRow 
                    label="Home Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'HomeWins'), safeGet(homeStandings, 'HomeLosses'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'HomeWins'), safeGet(awayStandings, 'HomeLosses'))} 
                />
                <StatRow 
                    label="Home Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'HomeWins', 0), safeGet(homeStandings, 'HomeLosses', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'HomeWins', 0), safeGet(awayStandings, 'HomeLosses', 0)))} 
                />
                <StatRow 
                    label="Away Record" 
                    homeValue={formatRecord(safeGet(homeStandings, 'AwayWins'), safeGet(homeStandings, 'AwayLosses'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'AwayWins'), safeGet(awayStandings, 'AwayLosses'))} 
                />
                <StatRow 
                    label="Away Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'AwayWins', 0), safeGet(homeStandings, 'AwayLosses', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'AwayWins', 0), safeGet(awayStandings, 'AwayLosses', 0)))} 
                />
            </TeamComparisonSection>

            <TeamComparisonSection title="Recent Performance">
                <StatRow 
                    label="Last 10" 
                    homeValue={formatRecord(safeGet(homeStandings, 'LastTenWins'), safeGet(homeStandings, 'LastTenLosses'))} 
                    awayValue={formatRecord(safeGet(awayStandings, 'LastTenWins'), safeGet(awayStandings, 'LastTenLosses'))} 
                />
                <StatRow 
                    label="Last 10 Win %" 
                    homeValue={formatPercentage(calculatePercentage(safeGet(homeStandings, 'LastTenWins', 0), safeGet(homeStandings, 'LastTenLosses', 0)))} 
                    awayValue={formatPercentage(calculatePercentage(safeGet(awayStandings, 'LastTenWins', 0), safeGet(awayStandings, 'LastTenLosses', 0)))} 
                />
            </TeamComparisonSection>
        </>
    );

    return (
        <div className="mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                {sportLower === 'mlb' && renderMLBSections()}
                {sportLower === 'nfl' && renderNFLSections()}
                {sportLower === 'nba' && renderNBASections()}
            </div>
        </div>
    );
};

export default TeamBreakdown;